import crc32 from 'crc32';
import { collection, doc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { nanoid } from 'nanoid/non-secure';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { trackAnalyticsVotedEvent } from '../utils/analytics';
import { firestore } from '../utils/firebase-config';
import * as Helper from '../utils/helper';
import * as storage from '../utils/storage';
import { STEPS } from '../utils/storage';
import Avatar from './Avatar';
import Form from './Form';
import Participant from './Participant';
import Rating from './Rating';
import SpeechBubble from './SpeechBubble';

export default function App(props) {
	// Collections: Must have an odd number of segments in the path.
	const databaseRoot = `${Helper.getHost()}/blog_feedback/data`;
	const [appData, setAppData] = useState({});
	const [scoreData, setScoreData] = useState({});
	const dialogRef = useRef(null);

	const [userData, setUserData] = useState({
		uid: crc32(`${Date.now()}-${nanoid()}`),
		step: storage.getVotedPosts().includes(props.postId) ? STEPS.VOTED : STEPS.QUESTION,
		selection: '',
		avatar: storage.getBlogAvatar(),
		modalOpen: false,
	});

	const getPostData = async () => {
		const collectionRef = collection(firestore, databaseRoot);
		const q = query(collectionRef, where('userId', '==', userData.uid), where('postId', '==', props.postId));
		const doc_refs = await getDocs(q);
		const res = [];

		doc_refs.forEach((post) => {
			res.push({
				...post.data(),
			});
		});

		const emojis = ['very_satisfied', 'satisfied', 'neutral', 'dissatisfied', 'very_dissatisfied'];
		const score = {};

		emojis.map((emote) => {
			score[emote] = res.length ? res.filter((entry) => entry.selectionId === emote).length : 0;
		});

		setScoreData(score);
	};

	const sendVoteToFirebase = async (selection) => {
		try {
			await setDoc(doc(firestore, databaseRoot, userData.uid), {
				contentHash: props.contentHash,
				postId: props.postId,
				postUrl: props.postUrl,
				selectionId: selection,
				timestamp: serverTimestamp(),
				language: props.language,
			});
		} catch (err) {
			console.log(err);
		}
	};

	const submitVote = (selection) => {
		storage.addVotedPost(props.postId);

		trackAnalyticsVotedEvent(selection);

		sendVoteToFirebase(selection);

		setScoreData((scoreData) => ({
			...scoreData,
			[selection]: scoreData[selection] + 1,
		}));

		setUserData((userData) => ({
			...userData,
			step: STEPS.EVALUATION,
			selection,
		}));
	};

	const sendFormToFirebase = async (comment) => {
		try {
			await updateDoc(doc(firestore, databaseRoot, userData.uid), {
				comment,
			});
		} catch (err) {
			console.log(err);
		}
	};

	const submitForm = (comment) => {
		sendFormToFirebase(comment);

		setUserData((userData) => ({
			...userData,
			step: STEPS.SUBMISSION,
		}));
	};

	const selectAvatar = (avatar) => {
		storage.setBlogAvatar(avatar);

		setUserData((userData) => ({
			...userData,
			avatar,
		}));
	};

	const getAvatarsPortal = () => {
		return createPortal(
			<dialog ref={dialogRef}>
				<form method="dialog">
					{appData.labels.modal_title}
					<button onClick={() => setUserData((userData) => ({ ...userData, modalOpen: false }))} className="close" />
				</form>
				<main>
					{Object.keys(appData.images.avatars).map((key) => {
						return (
							<div
								tabIndex={0}
								onClick={() => selectAvatar(key)}
								key={key}
								className={`blog-feedback__avatar${userData.avatar === key ? ' blog-feedback__avatar--active' : ''}`}
							>
								<img src={appData.images.avatars[key]} alt="" />
							</div>
						);
					})}
				</main>
			</dialog>,
			document.getElementById('blog-feedback__dialog'),
		);
	};

	useEffect(() => {
		// Fetch App Data via provided REST URL.
		fetch(props.dataUrl)
			.then((res) => res.json())
			.then((data) => {
				setAppData({ ...appData, ...data });
			})
			.catch((error) => console.error(error));

		// Update the appDate when the user has made a selection.
		getPostData();
	}, []);

	useEffect(() => {
		if (userData.selection !== '') {
			setTimeout(() => {
				setUserData((userData) => ({
					...userData,
					step: STEPS.FEEDBACK,
				}));
			}, 2500);
		}
	}, [userData.selection]);

	const canRender = () => {
		return appData.labels && appData.images && Object.keys(appData.images).length === 2 && Object.keys(scoreData).length;
	};

	return canRender() ? (
		<>
			<Participant alignment="right">
				<Avatar img={appData.images.us} />
				<SpeechBubble
					who={appData.labels.us || ''}
					text={[STEPS.EVALUATION, STEPS.FEEDBACK].includes(userData.step) ? appData.labels.steps[STEPS.EVALUATION] : appData.labels.steps[userData.step]}
				></SpeechBubble>
			</Participant>
			{![STEPS.SUBMISSION, STEPS.VOTED].includes(userData.step) && (
				<Participant>
					<Avatar
						openModal={() => dialogRef.current.showModal()}
						img={appData.images.avatars[userData.avatar]}
						canChangeAvatar={Object.keys(appData.images.avatars).length > 1}
					/>
					<SpeechBubble who={appData.labels.you}>
						{[STEPS.QUESTION, STEPS.EVALUATION, STEPS.FEEDBACK].includes(userData.step) && (
							<Rating score={scoreData} userData={userData} submitVote={submitVote} />
						)}
						{userData.step === STEPS.FEEDBACK && <Form submitForm={submitForm} labels={appData.labels} />}
					</SpeechBubble>
				</Participant>
			)}
			{getAvatarsPortal()}
		</>
	) : null;
}

App.propTypes = {
	contentHash: PropTypes.string,
	dataUrl: PropTypes.string,
	postId: PropTypes.number,
	postUrl: PropTypes.string,
	language: PropTypes.string,
};
